import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layout, Typography, Card, List, Input, Segmented, Select, Spin, Alert, App, Drawer, Tag,
  Row, Col, Statistic, Table, Empty, Button,
} from 'antd';
import {
  ArrowDownOutlined, ArrowUpOutlined, MinusOutlined, SearchOutlined, RightOutlined,
} from '@ant-design/icons';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  getProdutosCompra, getFichaProduto, getResumoInsights, getRelatorioCompras,
} from '../../services/comprasService';
import { getErrorMessage } from '../../utils/errorHandler';
import MonthNavigator from '../../components/MonthNavigator/MonthNavigator';

const { Content } = Layout;
const { Title, Text } = Typography;

const brl = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtData = (d) => (d ? dayjs(String(d).slice(0, 10)).format('DD/MM/YYYY') : '—');

const PALETA = ['#1F4F4F', '#B89040', '#50B0A0', '#2F6B6B', '#D4A84B', '#7BC4B8'];

const TEND = {
  subindo: { icon: <ArrowUpOutlined style={{ color: '#D64545' }} />, label: 'subindo' },
  caindo: { icon: <ArrowDownOutlined style={{ color: '#2F9E6F' }} />, label: 'caindo' },
  estavel: { icon: <MinusOutlined style={{ color: '#999' }} />, label: 'estável' },
};

function HistoricoComprasPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [produtos, setProdutos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busca, setBusca] = useState('');
  const [ordenar, setOrdenar] = useState('mais_comprados');

  const [fichaOpen, setFichaOpen] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [fichaLoading, setFichaLoading] = useState(false);

  const [aba, setAba] = useState('produtos');
  const [mesRef, setMesRef] = useState(dayjs().format('YYYY-MM'));
  const [relatorio, setRelatorio] = useState(null);
  const [relLoading, setRelLoading] = useState(false);

  const fetchProdutos = useCallback(async () => {
    try {
      setLoading(true);
      setProdutos(await getProdutosCompra({ ordenar }));
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao carregar histórico.'));
    } finally {
      setLoading(false);
    }
  }, [ordenar]);

  useEffect(() => { fetchProdutos(); }, [fetchProdutos]);

  useEffect(() => {
    if (aba !== 'mes') return;
    setRelLoading(true);
    getRelatorioCompras(mesRef)
      .then(setRelatorio)
      .catch((err) => message.error(getErrorMessage(err, 'Falha ao carregar o relatório.')))
      .finally(() => setRelLoading(false));
  }, [aba, mesRef, message]);
  useEffect(() => { getResumoInsights().then(setResumo).catch(() => {}); }, []);

  const visiveis = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return t ? produtos.filter((p) => p.nome.toLowerCase().includes(t)) : produtos;
  }, [produtos, busca]);

  const abrirFicha = async (produto) => {
    setFichaOpen(true);
    setFicha(null);
    setFichaLoading(true);
    try {
      setFicha(await getFichaProduto(produto.chave));
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao abrir a ficha.'));
      setFichaOpen(false);
    } finally {
      setFichaLoading(false);
    }
  };

  const { chartData, locais } = useMemo(() => {
    if (!ficha?.serie?.length) return { chartData: [], locais: [] };
    const locaisSet = [...new Set(ficha.serie.map((s) => s.local))];
    const porData = {};
    ficha.serie.forEach((s) => {
      const key = String(s.data).slice(0, 10);
      porData[key] = porData[key] || { data: key, label: fmtData(s.data) };
      porData[key][s.local] = Number(s.preco);
    });
    return {
      chartData: Object.values(porData).sort((a, b) => a.data.localeCompare(b.data)),
      locais: locaisSet,
    };
  }, [ficha]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
        <Spin size="large" tip="Carregando..." />
      </div>
    );
  }

  return (
    <Content style={{ padding: 'clamp(1rem, 4vw, 2rem)', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Title level={2} style={{ margin: 0 }}>Histórico de compras</Title>
      <div>
        <Text type="secondary">Onde cada produto sai mais barato, e como o preço variou.</Text>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ margin: '16px 0' }} />}

      <Segmented
        block
        style={{ marginTop: 16, maxWidth: 360 }}
        value={aba}
        onChange={setAba}
        options={[
          { label: 'Produtos', value: 'produtos' },
          { label: 'Por mês', value: 'mes' },
        ]}
      />

      {aba === 'mes' ? (
        <RelatorioMensal
          mesRef={mesRef}
          onMes={setMesRef}
          relatorio={relatorio}
          loading={relLoading}
          onAbrirLista={(id) => navigate(`/listas-compras/${id}`)}
        />
      ) : (
      <>
      {resumo && <VisaoGeral resumo={resumo} onAbrirLista={(id) => navigate(`/listas-compras/${id}`)} />}

      <style>{`
        .hc-produto-row { transition: background 0.15s; }
        .hc-produto-row:hover { background: #faf7f0; }
        .hc-produto-row:hover .hc-produto-nome { text-decoration: underline; }
        .hc-produto-row:hover .hc-produto-chevron { transform: translateX(2px); }
        .hc-produto-chevron { transition: transform 0.15s; }
      `}</style>
      <Card style={{ marginTop: 24 }} styles={{ body: { padding: 0 } }}>
        <div style={{ padding: 16, display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0' }}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder="Buscar produto"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ flex: '1 1 180px', minWidth: 0 }}
          />
          <Select
            value={ordenar}
            onChange={setOrdenar}
            style={{ flex: '0 1 190px', minWidth: 150 }}
            options={[
              { label: 'Mais comprados', value: 'mais_comprados' },
              { label: 'Maior variação', value: 'maior_variacao' },
              { label: 'Ordem alfabética', value: 'nome' },
            ]}
          />
        </div>
        {visiveis.length > 0 && (
          <div style={{ padding: '8px 20px', fontSize: 12, color: '#8c8c8c', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
            Toque num produto para ver onde está mais barato, o mais caro e o histórico de preços.
          </div>
        )}
        <List
          dataSource={visiveis}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhum produto no histórico ainda. Conclua uma compra com preços preenchidos."
              />
            ),
          }}
          renderItem={(p) => (
            <List.Item
              className="hc-produto-row"
              style={{ padding: '14px 20px', cursor: 'pointer' }}
              onClick={() => abrirFicha(p)}
            >
              <List.Item.Meta
                title={
                  <span className="hc-produto-nome" style={{ color: '#1F4F4F', fontWeight: 600 }}>
                    {p.nome}
                  </span>
                }
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {p.compras} compra(s) · mais barato {brl(p.menor?.preco)} em {p.menor?.local}
                  </Text>
                }
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ textAlign: 'right' }}>
                  <Text strong>{brl(p.ultimo?.preco)}</Text>
                  <div><Text type="secondary" style={{ fontSize: 11 }}>último preço</Text></div>
                </div>
                <RightOutlined className="hc-produto-chevron" style={{ color: '#bfbfbf', fontSize: 12 }} />
              </div>
            </List.Item>
          )}
        />
      </Card>
      </>
      )}

      <Drawer
        title={ficha?.nome || 'Ficha do produto'}
        open={fichaOpen}
        onClose={() => setFichaOpen(false)}
        width={window.innerWidth <= 768 ? '100%' : 560}
      >
        {fichaLoading || !ficha ? (
          <Spin />
        ) : (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="Mais barato" value={brl(ficha.menor?.preco)} valueStyle={{ color: '#2F9E6F', fontSize: 20 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ficha.menor?.local} · {fmtData(ficha.menor?.data)}
                </Text>
              </Col>
              <Col span={12}>
                <Statistic title="Mais caro" value={brl(ficha.maior?.preco)} valueStyle={{ color: '#D64545', fontSize: 20 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {ficha.maior?.local} · {fmtData(ficha.maior?.data)}
                </Text>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}><Statistic title="Preço médio" value={brl(ficha.preco_medio)} valueStyle={{ fontSize: 16 }} /></Col>
              <Col span={8}><Statistic title="Compras" value={ficha.compras} valueStyle={{ fontSize: 16 }} /></Col>
              <Col span={8}><Statistic title="Último" value={brl(ficha.ultimo?.preco)} valueStyle={{ fontSize: 16 }} /></Col>
            </Row>

            {ficha.avaliacao_ultimo && (
              <Alert
                style={{ marginTop: 16 }}
                showIcon
                type={
                  ficha.avaliacao_ultimo.nivel === 'melhor' || ficha.avaliacao_ultimo.nivel === 'bom'
                    ? 'success'
                    : ficha.avaliacao_ultimo.nivel === 'caro'
                    ? 'warning'
                    : 'info'
                }
                message={`Último preço (${brl(ficha.ultimo?.preco)}): ${ficha.avaliacao_ultimo.texto}`}
              />
            )}

            {Number(ficha.economia_perdida) > 0 && (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message={`Comprando sempre no mais barato, você teria economizado ${brl(ficha.economia_perdida)} nas ${ficha.compras} compras deste item.`}
              />
            )}

            {chartData.length > 1 && (
              <div style={{ marginTop: 24 }}>
                <Text strong>Preço ao longo do tempo</Text>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip formatter={(v) => brl(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {locais.map((loc, i) => (
                      <Line
                        key={loc}
                        type="monotone"
                        dataKey={loc}
                        stroke={PALETA[i % PALETA.length]}
                        strokeWidth={2}
                        connectNulls
                        dot={{ r: 3 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <Text strong>Por mercado</Text>
              <Table
                style={{ marginTop: 8 }}
                size="small"
                pagination={false}
                rowKey="local_id"
                dataSource={ficha.por_local || []}
                columns={[
                  { title: 'Mercado', dataIndex: 'local' },
                  { title: 'Último', dataIndex: 'ultimo_preco', align: 'right', render: brl },
                  { title: 'Médio', dataIndex: 'preco_medio', align: 'right', render: brl },
                  { title: 'Compras', dataIndex: 'compras', align: 'right' },
                  {
                    title: 'Tend.',
                    dataIndex: 'tendencia',
                    align: 'center',
                    render: (t) => (
                      <Tag icon={(TEND[t] || TEND.estavel).icon} style={{ margin: 0 }}>
                        {(TEND[t] || TEND.estavel).label}
                      </Tag>
                    ),
                  },
                ]}
              />
            </div>
          </>
        )}
      </Drawer>
    </Content>
  );
}

function VisaoGeral({ resumo, onAbrirLista }) {
  const { cesta, melhor_mercado: melhor, compras } = resumo;
  const temAlgo =
    cesta?.variacao_3m_pct != null || melhor || (compras?.mais_caras?.length > 0);
  if (!temAlgo) return null;

  return (
    <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
      {cesta?.variacao_3m_pct != null && (
        <Col xs={24} md={8}>
          <Card size="small" title="Índice da sua cesta">
            <Statistic
              value={cesta.variacao_3m_pct}
              precision={1}
              suffix="%"
              prefix={cesta.variacao_3m_pct >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              valueStyle={{ color: cesta.variacao_3m_pct >= 0 ? '#D64545' : '#2F9E6F' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              nos últimos ~3 meses ({cesta.n_produtos} produtos)
              {cesta.variacao_6m_pct != null && ` · ${cesta.variacao_6m_pct}% em 6 meses`}
            </Text>
            <div style={{ marginTop: 8 }}>
              {(cesta.detalhe || []).slice(0, 3).map((d) => (
                <div key={d.nome} style={{ fontSize: 12 }}>
                  <Text type="secondary">{d.nome}: </Text>
                  <Text style={{ color: d.var_pct >= 0 ? '#D64545' : '#2F9E6F' }}>
                    {d.var_pct >= 0 ? '+' : ''}{d.var_pct}%
                  </Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      )}

      {melhor && (
        <Col xs={24} md={8}>
          <Card size="small" title="Onde vale a pena no geral">
            <Text strong style={{ fontSize: 16 }}>{melhor.local}</Text>
            {Number(melhor.economia_estimada) > 0 && (
              <div><Text type="secondary" style={{ fontSize: 12 }}>
                economia potencial ~{brl(melhor.economia_estimada)} comprando sempre no mais barato
              </Text></div>
            )}
            <div style={{ marginTop: 8 }}>
              {(melhor.ranking || []).map((r) => (
                <div key={r.local_id} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">{r.local}</Text>
                  <Text>índice {r.indice}</Text>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      )}

      {compras?.mais_caras?.length > 0 && (
        <Col xs={24} md={8}>
          <Card size="small" title="Compras">
            <Text type="secondary" style={{ fontSize: 12 }}>
              Ticket médio: {brl(compras.ticket_medio_geral)}
            </Text>
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <Text strong>Mais cara: </Text>
              {compras.mais_caras[0] && (
                <Button type="link" style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  onClick={() => onAbrirLista(compras.mais_caras[0].lista_id)}>
                  {compras.mais_caras[0].local} — {brl(compras.mais_caras[0].total)}
                </Button>
              )}
            </div>
            <div style={{ fontSize: 12 }}>
              <Text strong>Mais barata: </Text>
              {compras.mais_baratas[0] && (
                <Button type="link" style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  onClick={() => onAbrirLista(compras.mais_baratas[0].lista_id)}>
                  {compras.mais_baratas[0].local} — {brl(compras.mais_baratas[0].total)}
                </Button>
              )}
            </div>
          </Card>
        </Col>
      )}
    </Row>
  );
}

function RelatorioMensal({ mesRef, onMes, relatorio, loading, onAbrirLista }) {
  const vazio = relatorio && relatorio.n_compras === 0;
  return (
    <div style={{ marginTop: 16 }}>
      <MonthNavigator initialDate={mesRef} onDateChange={onMes} />

      {loading || !relatorio ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
      ) : vazio ? (
        <Empty style={{ marginTop: 24 }} description="Nenhuma compra concluída neste mês." />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Total no mês" value={brl(relatorio.total)} valueStyle={{ fontSize: 20 }} />
                {relatorio.variacao_pct != null && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {relatorio.variacao_pct >= 0 ? '+' : ''}{relatorio.variacao_pct}% vs mês anterior
                  </Text>
                )}
              </Card>
            </Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Compras" value={relatorio.n_compras} valueStyle={{ fontSize: 20 }} /></Card></Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Ticket médio" value={brl(relatorio.ticket_medio)} valueStyle={{ fontSize: 20 }} /></Card></Col>
            <Col xs={12} md={6}><Card size="small"><Statistic title="Mês anterior" value={brl(relatorio.total_mes_anterior)} valueStyle={{ fontSize: 16 }} /></Card></Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card size="small" title="Por mercado">
                <Table
                  size="small"
                  pagination={false}
                  rowKey="local"
                  dataSource={relatorio.por_local}
                  columns={[
                    { title: 'Mercado', dataIndex: 'local' },
                    { title: 'Compras', dataIndex: 'compras', align: 'right' },
                    { title: 'Total', dataIndex: 'total', align: 'right', render: brl },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" title="Produtos que mais pesaram no gasto">
                {relatorio.top_produtos.length === 0 ? (
                  <Text type="secondary">Sem itens precificados neste mês.</Text>
                ) : relatorio.top_produtos.map((p) => (
                  <div key={p.nome} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                    <Text>{p.nome}</Text>
                    <Text type="secondary">{brl(p.total)} · {p.pct}%</Text>
                  </div>
                ))}
              </Card>
            </Col>
          </Row>

          <Card size="small" title="Compras do mês" style={{ marginTop: 16 }} styles={{ body: { padding: 0 } }}>
            <List
              dataSource={relatorio.compras}
              renderItem={(c) => (
                <List.Item style={{ padding: '10px 16px', cursor: 'pointer' }} onClick={() => onAbrirLista(c.lista_id)}>
                  <List.Item.Meta
                    title={c.titulo}
                    description={<Text type="secondary" style={{ fontSize: 12 }}>{c.local} · {fmtData(c.data)}</Text>}
                  />
                  <Text strong>{brl(c.total)}</Text>
                </List.Item>
              )}
            />
          </Card>
        </>
      )}
    </div>
  );
}

export default HistoricoComprasPage;
